import React, { useState, useEffect, useCallback } from 'react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { ASINReviewFormData, ASINReviewFormProps, createDefaultFormData } from '../../types/asinReview';
import { PMT3LoadingState } from '../../types/pmt3Types';
import { pmt3WriteService } from '../../services/pmt3WriteService';
import { usePMT3Selection } from '../../context/PMT3SelectionContext';
import { pmt2Service } from '../../services/pmt2Service';

const STORAGE_KEY = 'pmt3_asin_review_form_data';

export const ASINReviewForm: React.FC<ASINReviewFormProps> = ({
  onFormChange,
  initialData
}) => {
  const [formData, setFormData] = useState<ASINReviewFormData>(() => {
    // Try to load from localStorage first, then use initialData, then defaults
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        return { ...createDefaultFormData(), ...parsedData };
      }
    } catch (error) {
      console.warn('Failed to load ASIN Review form data from localStorage:', error);
    }
    
    return { ...createDefaultFormData(), ...initialData };
  });

  const [isDirty, setIsDirty] = useState(false);
  const [loadingState, setLoadingState] = useState<PMT3LoadingState>({ isLoading: false });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [spreadsheetId, setSpreadsheetId] = useState<string>('');

  // Get current selection from PMT3 context
  const { selectedCompany, selectedProduct } = usePMT3Selection();
  const currentASIN = selectedProduct?.id || '';

  // Load spreadsheet ID based on selected company
  useEffect(() => {
    const loadSpreadsheetId = async () => {
      if (!selectedCompany) {
        setSpreadsheetId('');
        return;
      }

      try {
        const configs = await pmt2Service.getSpreadsheetConfigs();
        const config = configs.find(c => c.companyName === selectedCompany);
        
        if (config) {
          setSpreadsheetId(config.spreadsheetId);
        } else {
          console.warn(`No spreadsheet configuration found for company: ${selectedCompany}`);
          setSpreadsheetId('');
        }
      } catch (error) {
        console.error('Error loading spreadsheet configuration:', error);
        setSpreadsheetId('');
      }
    };

    loadSpreadsheetId();
  }, [selectedCompany]);

  // Save to localStorage whenever form data changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.warn('Failed to save ASIN Review form data to localStorage:', error);
    }
  }, [formData]);

  // Call onFormChange when form data changes
  useEffect(() => {
    if (onFormChange) {
      onFormChange(formData);
    }
  }, [formData, onFormChange]);

  const updateField = useCallback(<K extends keyof ASINReviewFormData>(
    field: K,
    value: ASINReviewFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  const updateLedgerEntry = useCallback((index: number, value: string) => {
    setFormData(prev => {
      const newEntries = [...prev.ledgerEntries];
      newEntries[index] = value;
      return { ...prev, ledgerEntries: newEntries };
    });
    setIsDirty(true);
  }, []);

  const addLedgerEntry = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      ledgerEntries: [...prev.ledgerEntries, '']
    }));
    setIsDirty(true);
  }, []);

  const removeLedgerEntry = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      ledgerEntries: prev.ledgerEntries.filter((_, i) => i !== index)
    }));
    setIsDirty(true);
  }, []);

  const updateOverallSituation = useCallback((index: number, value: string) => {
    setFormData(prev => {
      const newEntries = [...prev.overallSituation];
      newEntries[index] = value;
      return { ...prev, overallSituation: newEntries };
    });
    setIsDirty(true);
  }, []);

  const addOverallSituation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      overallSituation: [...prev.overallSituation, '']
    }));
    setIsDirty(true);
  }, []);

  const removeOverallSituation = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      overallSituation: prev.overallSituation.filter((_, i) => i !== index)
    }));
    setIsDirty(true);
  }, []);

  const handleNewPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value === '' ? null : parseFloat(value);
    updateField('newPrice', numericValue);
  }, [updateField]);

  const handleClear = useCallback(() => {
    const defaultData = createDefaultFormData();
    setFormData(defaultData);
    setIsDirty(false);
    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear ASIN Review form data from localStorage:', error);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      // Clear previous messages
      setValidationErrors([]);
      setSuccessMessage('');
      
      // Client-side validation
      const validation = pmt3WriteService.validateFormClientSide(formData);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return;
      }

      // Check required context data
      if (!selectedCompany) {
        setValidationErrors(['No company selected. Please select a company first.']);
        return;
      }

      if (!spreadsheetId) {
        setValidationErrors([`No spreadsheet configuration found for company: ${selectedCompany}. Please configure the spreadsheet in PMT2 settings.`]);
        return;
      }

      if (!currentASIN) {
        setValidationErrors(['No ASIN selected. Please select a product first.']);
        return;
      }

      // Set loading state
      setLoadingState({
        isLoading: true,
        operation: 'Completing ASIN Review',
        message: 'Submitting data to Google Sheets and Firestore...'
      });

      // Call PMT3 Write Service with proper parameters
      const result = await pmt3WriteService.completeASINReview(formData, spreadsheetId, currentASIN);

      if (result.success) {
        setSuccessMessage('ASIN Review completed successfully! Data has been saved to both Google Sheets and Firestore.');
        
        // Clear form after successful submission
        const defaultData = createDefaultFormData();
        setFormData(defaultData);
        setIsDirty(false);
        
        // Clear localStorage
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          console.warn('Failed to clear localStorage:', error);
        }

        // Log success
        console.log('ASIN Review completed:', result.data);
      } else {
        setValidationErrors([result.error || 'Failed to complete ASIN Review']);
      }

    } catch (error) {
      console.error('Error submitting ASIN Review:', error);
      setValidationErrors([
        error instanceof Error ? error.message : 'An unexpected error occurred while submitting the form'
      ]);
    } finally {
      setLoadingState({ isLoading: false });
    }
  }, [formData, spreadsheetId, currentASIN, selectedCompany]);

  // Handle Client Notes submission
  const handleClientNotesSubmit = useCallback(async (notes: string) => {
    try {
      setLoadingState({
        isLoading: true,
        operation: 'Pasting Client Notes',
        message: 'Updating Google Sheets...'
      });

      const result = await pmt3WriteService.pasteClientNotes(notes, spreadsheetId);
      
      if (result.success) {
        setSuccessMessage('Client Notes pasted successfully!');
      } else {
        setValidationErrors([result.error || 'Failed to paste Client Notes']);
      }
    } catch (error) {
      console.error('Error pasting Client Notes:', error);
      setValidationErrors([
        error instanceof Error ? error.message : 'Failed to paste Client Notes'
      ]);
    } finally {
      setLoadingState({ isLoading: false });
    }
  }, [spreadsheetId]);

  return (
    <div className="px-4 py-2 space-y-3">
      {/* Section Title */}
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          ASIN Review
        </h3>
      </div>

      {/* Ledger Entry Section */}
      <Collapsible defaultOpen>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-1 group">
            <ChevronDown className="h-3 w-3 text-gray-500 transition-transform group-data-[state=closed]:-rotate-90" />
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
              Ledger Entry
            </label>
          </CollapsibleTrigger>
          <button
            type="button"
            onClick={addLedgerEntry}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            + Add Row
          </button>
        </div>
        <CollapsibleContent className="space-y-1 mt-1">
          {formData.ledgerEntries.map((entry, index) => (
            <div key={`ledger-${index}`} className="flex items-start gap-1">
              <Textarea
                value={entry}
                onChange={(e) => updateLedgerEntry(index, e.target.value)}
                placeholder=""
                className="flex-1 text-xs resize-y min-h-[32px] h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              {formData.ledgerEntries.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLedgerEntry(index)}
                  className="mt-1 text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 leading-none"
                  title="Remove row"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-gray-700" />

      {/* Overall Situation Section */}
      <Collapsible defaultOpen>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-1 group">
            <ChevronDown className="h-3 w-3 text-gray-500 transition-transform group-data-[state=closed]:-rotate-90" />
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
              Overall Situation
            </label>
          </CollapsibleTrigger>
          <button
            type="button"
            onClick={addOverallSituation}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            + Add Row
          </button>
        </div>
        <CollapsibleContent className="space-y-1 mt-1">
          {formData.overallSituation.map((entry, index) => (
            <div key={`situation-${index}`} className="flex items-start gap-1">
              <Textarea
                value={entry}
                onChange={(e) => updateOverallSituation(index, e.target.value)}
                placeholder=""
                className="flex-1 text-xs resize-y min-h-[32px] h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              {formData.overallSituation.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeOverallSituation(index)}
                  className="mt-1 text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 leading-none"
                  title="Remove row"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-gray-700" />

      {/* Review Details Section */}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="flex items-center gap-1 group">
          <ChevronDown className="h-3 w-3 text-gray-500 transition-transform group-data-[state=closed]:-rotate-90" />
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            Review Details
          </label>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 mt-1">
          {/* New GPO */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              New GPO
            </label>
            <Input
              type="text"
              value={formData.newGPO}
              onChange={(e) => updateField('newGPO', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* New Target ACoS */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Target ACoS
            </label>
            <Input
              type="text"
              value={formData.newTargetACoS}
              onChange={(e) => updateField('newTargetACoS', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* New Max Bid */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Max Bid
            </label>
            <Input
              type="text"
              value={formData.newMaxBid}
              onChange={(e) => updateField('newMaxBid', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* New Price */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Price
            </label>
            <Input
              type="number"
              step="0.01"
              value={formData.newPrice?.toString() || ''}
              onChange={handleNewPriceChange}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* PPC Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              PPC Status
            </label>
            <Input
              type="text"
              value={formData.ppcStatus}
              onChange={(e) => updateField('ppcStatus', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* ASIN Review Cadence */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              ASIN Review Cadence
            </label>
            <Input
              type="text"
              value={formData.asinReviewCadence}
              onChange={(e) => updateField('asinReviewCadence', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-gray-700" />

      {/* Settings Section */}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="flex items-center gap-1 group">
          <ChevronDown className="h-3 w-3 text-gray-500 transition-transform group-data-[state=closed]:-rotate-90" />
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            Settings
          </label>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 mt-1">
          {/* Name Override */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name Override
            </label>
            <Input
              type="text"
              value={formData.nameOverride}
              onChange={(e) => updateField('nameOverride', e.target.value)}
              placeholder=""
              className="w-full text-xs h-8 py-1 px-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Time
            </label>
            <Input
              type="text"
              value={formData.startTime}
              readOnly
              className="w-full text-xs h-8 py-1 px-2 bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 cursor-not-allowed"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Loading State */}
      {loadingState.isLoading && (
        <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-3 w-3 border border-blue-500 border-t-transparent rounded-full"></div>
            <span className="text-blue-700 dark:text-blue-300">
              {loadingState.operation || 'Processing...'}
            </span>
          </div>
          {loadingState.message && (
            <div className="text-blue-600 dark:text-blue-400 mt-1">
              {loadingState.message}
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs">
          <div className="text-green-700 dark:text-green-300">
            ✓ {successMessage}
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs">
          <div className="text-red-700 dark:text-red-300 font-medium mb-1">
            Please fix the following errors:
          </div>
          <ul className="text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Configuration Status */}
      {!selectedCompany && (
        <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
          <div className="text-yellow-700 dark:text-yellow-300">
            ⚠️ No company selected. Please select a company first.
          </div>
        </div>
      )}

      {selectedCompany && !spreadsheetId && (
        <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
          <div className="text-yellow-700 dark:text-yellow-300">
            ⚠️ No spreadsheet configuration found for company: {selectedCompany}. Please configure the spreadsheet in PMT2 settings.
          </div>
        </div>
      )}

      {/* Current ASIN Display */}
      {currentASIN && (
        <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs">
          <div className="text-gray-700 dark:text-gray-300">
            Current ASIN: <span className="font-mono font-medium">{currentASIN}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={loadingState.isLoading}
          className="flex-1 text-xs h-8 py-1 px-3 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={loadingState.isLoading || !spreadsheetId}
          className="flex-1 text-xs h-8 py-1 px-3 bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loadingState.isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </div>

      {/* Status Indicators */}
      <div className="flex items-center justify-between mt-2 text-xs">
        {/* Dirty indicator */}
        {isDirty && !loadingState.isLoading && (
          <div className="text-orange-600 dark:text-orange-400">
            • Unsaved changes
          </div>
        )}
        
        {/* Connection status */}
        <div className="text-gray-500 dark:text-gray-400 ml-auto">
          {spreadsheetId ? '🟢 Connected' : '🔴 Not configured'}
        </div>
      </div>
    </div>
  );
};

export default ASINReviewForm;